// Added by Jonghyung Lee on 2021-02-08
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, useState, useEffect, useMemo, useContext, ReactNode, ReactText, forwardRef } from 'react'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import ReactMarkdown from 'react-markdown'
import emoji from 'node-emoji'
import { makeStyles, createStyles } from '@material-ui/core'
// Context
import { NotiStackContext } from '../NotiStack/NotiStackProvider'

// ui
import { Paper, Typography, Card, CardActions, IconButton, CardProps } from '@/components/_primes/v2'
import { ConfirmDialog } from '@/components/common/v2/Dialog/ConfirmDialog'
import { CloseIcon, CheckIcon } from '@/components/_primes'

// helper
import Intl from '@/helper/Intl'
import { getBaseUrl, downloadFile } from '@/helper/util'
import { NOTIFICATION_AUTO_HIDE_DURATION, ReqGetNotifications } from '@/helper/define'
import useStore from '@/stores/global/hooks/useStore'
import Logger from '@/helper/Logger'
interface NotificationsItemProps {
  /** NotiStack에서 필요한 ID와 Message */
  id?: ReactText
  message: ReactNode
  /** Drawer에서 표시될 Item인지, Popup으로 나타내는 Item인지 구분 (X 버튼 처리를 위해서) */
  isPopUp?: boolean
  /** Notification center server에서 생성된 id */
  notificationId: string
  /** Notification created at */
  createdAt?: string
  /** Read / Unread */
  isRead?: boolean
  /** 삭제 후 Get 하도록 */
  handleInvokeQuery?: React.Dispatch<React.SetStateAction<ReqGetNotifications>>
}

const NotificationsItem: FC<NotificationsItemProps> = forwardRef<CardProps, NotificationsItemProps>(
  (
    { id, message: originMessage, notificationId, createdAt, isRead, isPopUp = false, handleInvokeQuery, ...props },
    ref
  ) => {
    const classes = useStyles()

    const { closeSnackbar } = useSnackbar()
    const { unsetNotiInfo, getNotiInfo, changeAutoHideDuration } = useContext(NotiStackContext)!

    const { userAuth, notification } = useStore()
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // RegExp
    // eslint-disable-next-line
    const i18nRegex = /\$i18n{.*}/
    // eslint-disable-next-line
    const regDiscardReservedWords = /(?![\$i18n{])[\w*.]+/
    // eslint-disable-next-line
    const regEmoji = /:\w+:/gi

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // state
    const [message, setMessage] = useState<string>(originMessage as string)
    const [notiTitle, setNotiTitle] = useState<string>()
    /** Dialog */
    const [open, setOpen] = useState<boolean>(false)
    const [dialogMessage, setDialogMessage] = useState<string>('')
    const [dialogTitleTheme, setDialogTitleTheme] = useState<'default' | 'confirm' | 'warning'>('confirm')

    useEffect(() => {
      changeAutoHideDuration(NOTIFICATION_AUTO_HIDE_DURATION)
      setMessage((m) =>
        m
          .split('\n')
          .map((item, index) => {
            if (index === 0) return setNotiTitle(item)
            return item
          })
          .join('\n')
      )
      if (isPopUp) {
        const curNotice = getNotiInfo(id as string)!
        if (!curNotice) return
      }
      // eslint-disable-next-line
    }, [isPopUp, id])

    const i18nText = useMemo(
      () => ({
        titleDialog: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsitem.title_dialog' }),
        tooltipClear: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsitem.tooltip_clear' }),
        tooltipMarkAsRead: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsitem.tooltip_mark_as_read' }),
        msgLinkInvalid: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsitem.msg_invalid_link' }),
        msgDeleteNotification: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsitem.msg_delete_notification' }),
      }),
      []
    )

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // handler
    const handleClickReadButton = async () => {
      try {
        await notification.updateElement(notificationId, { read: true })
      } catch (error) {
        Logger.errLog(`[NotificationsItem] Update Failed : ${error}`)
      }
    }

    const handleClickCloseButton = async (e: any) => {
      e.stopPropagation()
      if (isPopUp) {
        unsetNotiInfo(id as string)
        closeSnackbar(id)
        if (notificationId.includes('unread')) return
        /** X를 눌러서 닫았으므로 읽음 처리 */
        try {
          await notification.updateElement(notificationId, { read: true })
        } catch (error) {
          Logger.errLog(`[NotificationsItem] Update Failed : ${error}`)
        }
        return
      }
      setOpen(true)
      setDialogMessage(i18nText.msgDeleteNotification)
      setDialogTitleTheme('confirm')
    }

    const handleClickLink = async (href: string) => {
      try {
        const isValid = await notification.checkElementUrl(notificationId, href)
        if (!isValid) {
          setOpen(true)
          setDialogMessage(i18nText.msgLinkInvalid)
          setDialogTitleTheme('warning')
          return
        }

        const BASE_URL = getBaseUrl().split('/api')[0]
        let link: string = ''

        if (message.includes('wps-result')) {
          const fileName = href.split('/').pop()
          link = `${BASE_URL + href}?access-token=${userAuth.accessToken}`
          return downloadFile(link, fileName!)
        }
        link = BASE_URL + href
        window.location.href = link
      } catch (error) {
        Logger.errLog(`[NotificationsItem] Update Failed : ${error}`)
      }
    }

    // handler for dialog
    const handleDialogCloseBtn = () => {
      handleInvokeQuery!((prev) => ({ ...prev }))
      setOpen(false)
    }

    const handleDialogConfirmBtn = async () => {
      try {
        await notification.deleteElement(notificationId)
        handleInvokeQuery!((prev) => ({ ...prev }))
        return setOpen(false)
      } catch (error) {
        Logger.errLog(`[NotificationsItem] Delete Failed : ${error}`)
      }
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Markdown Custom Renderers
    const LinkRenderer = ({ href, children }: any) => (
      <span className={classes.link} onClick={() => handleClickLink(href)}>
        {children}
      </span>
    )

    const BlockquoteRenderer = ({ children }: any) => <div className={classes.blockQuote}>{children}</div>

    const TextRenderer = ({ value }: any) => {
      const replacedMsg = value
        .replace(regEmoji, (emo: any) => emoji.get(emo))
        .replace(i18nRegex, (i18n: any) => Intl.getIntlText({ id: regDiscardReservedWords.exec(i18n)![0] }))
      return replacedMsg
    }

    return (
      <>
        <Card ref={ref} className={classes.card} square noti-type={isPopUp.toString()}>
          <CardActions className={classes.cardActionWrapper}>
            <Typography className={classes.typography} variant={'inherit'}>
              <ReactMarkdown source={notiTitle as string} />
            </Typography>
            <div className={classes.iconButtonArea}>
              {!isPopUp && !isRead && (
                <IconButton title={i18nText.tooltipMarkAsRead} size='small' onClick={handleClickReadButton}>
                  <CheckIcon fontSize='small' />
                </IconButton>
              )}
              <IconButton
                title={isPopUp ? i18nText.tooltipMarkAsRead : i18nText.tooltipClear}
                size='small'
                onClick={handleClickCloseButton}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
            </div>
          </CardActions>
          <Paper className={classes.markdownArea} square>
            {/* source에 tab 등의 indentation이 들어가면 Parsing되지 않음 */}
            <ReactMarkdown
              source={message as string}
              renderers={{ link: LinkRenderer, blockquote: BlockquoteRenderer, text: TextRenderer }}
            />
            {!isPopUp && <div className={classes.dateArea}>{dayjs(createdAt).format('YYYY/MM/DD HH:mm:ss')}</div>}
          </Paper>
        </Card>
        <ConfirmDialog
          messages={dialogMessage}
          titleTheme={dialogTitleTheme}
          title={i18nText.titleDialog}
          open={open}
          hasCancelBtn
          onConfirmBtnClick={handleDialogConfirmBtn}
          onCloseBtnClick={handleDialogCloseBtn}
        />
      </>
    )
  }
)

const useStyles = makeStyles(() =>
  createStyles({
    card: {
      marginBottom: '1rem',
      userSelect: 'text',
      width: 370,
      '&[noti-type="false"]': {
        marginBottom: '1rem',
      },
      '&[noti-type="true"]': {
        boxShadow: '0.5px 0.5px 10px 1px black',
      },
    },
    typography: {
      marginLeft: '0 !important',
      flex: 1,
      fontSize: '1rem',
      fontWeight: 'bold',
    },
    iconWrapper: {
      display: 'flex',
      opacity: '0.9',
      padding: '7px 0',
      fontSize: '22px',
      marginRight: '12px',
    },
    cardActionWrapper: {
      padding: '16px 8px 4px 16px',
      height: '33px',
      backgroundColor: 'rgb(49, 49, 49)',
    },
    iconButtonArea: {
      marginLeft: 'auto',
      opacity: '0.5',
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
    markdownArea: {
      padding: '0 20px 8px',
      backgroundColor: 'rgb(49, 49, 49)',
      '& p': {
        color: '#dcdcdc',
        lineHeight: 1.8,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
      },
      '& > p': {
        paddingTop: '6px',
        margin: '0 0 6px',
      },
      '& > div > p': {
        margin: 0,
      },
    },
    blockQuote: {
      padding: '.25rem 0 .25rem 1rem',
      borderLeft: '3px solid #000',
    },
    link: {
      color: '#1c94c4',
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    dateArea: {
      display: 'flex',
      justifyContent: 'flex-end',
      height: '22px',
      opacity: 0.5,
    },
    checkIcon: {
      fontSize: 20,
      color: '#b3b3b3',
      paddingRight: 4,
    },
  })
)

export default NotificationsItem
